import type {
  UpdateUserPreferences,
  UserPreferences,
} from '@src/dto/UserPreferences';
import { isRight } from 'fp-ts/Either';
import { createContext, type ReactNode, useEffect, useRef } from 'react';
import { createRequiredContextHook } from '../hooks/createRequiredContextHook';
import { useAsyncData } from '../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../hooks/useAuth';
import { useTRPC } from '../hooks/useTRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { safeLocalStorage } from '../utils/safeLocalStorage';
import { isNonJsonTrpcTransportError } from '../utils/trpcTransport';
import {
  applyUserPreferenceUpdates,
  getDefaultUserPreferences,
  normalizeUserPreferences,
} from '../utils/userPreferences';

interface PreferencesContextValue {
  preferences: UserPreferences;
  isLoading: boolean;
  error: string | null;
  updatePreferences: (updates: UpdateUserPreferences) => Promise<void>;
}

const defaultPreferences = getDefaultUserPreferences();

const PREFERENCES_STORAGE_KEY_PREFIX = 'user_preferences';

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const [authState] = useAuth();
  const activePreferenceStorageKey =
    authState.type === AuthStateType.Authenticated
      ? `${PREFERENCES_STORAGE_KEY_PREFIX}:${authState.user.id}`
      : null;
  const {
    data: preferences,
    setData: setPreferences,
    isLoading,
    setIsLoading,
    error,
    setError,
    invalidate,
    run,
  } = useAsyncData<UserPreferences>({
    initialData: defaultPreferences,
  });
  const activePreferenceStorageKeyRef = useRef(activePreferenceStorageKey);
  const canAccessPreferences =
    authState.type === AuthStateType.Authenticated &&
    authState.user.onboardingState !== 'EmailVerification';

  useEffect(() => {
    activePreferenceStorageKeyRef.current = activePreferenceStorageKey;
  }, [activePreferenceStorageKey]);

  useEffect(() => {
    if (!activePreferenceStorageKey) {
      setPreferences(defaultPreferences);
      return;
    }

    try {
      const stored = safeLocalStorage.getItem(activePreferenceStorageKey);
      if (stored) {
        const normalizedPreferences = normalizeUserPreferences(
          JSON.parse(stored),
        );
        setPreferences(normalizedPreferences);
        safeLocalStorage.setItem(
          activePreferenceStorageKey,
          JSON.stringify(normalizedPreferences),
        );
        return;
      }
    } catch (e) {
      captureAndReportError(e, {
        operation: 'Failed to load preferences from localStorage',
        component: 'PreferencesProvider',
      });
    }

    setPreferences(defaultPreferences);
  }, [activePreferenceStorageKey]);

  const queryServerPreferences = async () => {
    try {
      return await trpc.preferences.getUserPreferences.query(undefined);
    } catch (error) {
      if (!isNonJsonTrpcTransportError(error)) {
        throw error;
      }

      return await trpc.getUserPreferences.query(undefined);
    }
  };

  const mutateServerPreferences = async (updates: UpdateUserPreferences) => {
    try {
      return await trpc.preferences.updateUserPreferences.mutate(updates);
    } catch (error) {
      if (!isNonJsonTrpcTransportError(error)) {
        throw error;
      }

      return await trpc.updateUserPreferences.mutate(updates);
    }
  };

  useEffect(() => {
    invalidate();

    if (!canAccessPreferences) {
      setIsLoading(false);
      setError(null);
      return;
    }

    void run(queryServerPreferences, {
      onSuccess: (serverPreferences, helpers) => {
        if ('_tag' in serverPreferences && isRight(serverPreferences)) {
          const normalizedPreferences = normalizeUserPreferences(
            serverPreferences.right,
          );
          helpers.setData(normalizedPreferences);
          if (activePreferenceStorageKey) {
            safeLocalStorage.setItem(
              activePreferenceStorageKey,
              JSON.stringify(normalizedPreferences),
            );
          }
          return;
        }

        captureAndReportError('Failed to parse server preferences', {
          component: 'PreferencesProvider',
        });
        helpers.setError('Failed to load preferences');
      },
      onError: (err, helpers) => {
        captureAndReportError(err, {
          operation: 'Failed to fetch user preferences',
          component: 'PreferencesProvider',
        });
        helpers.setError('Failed to load preferences');
      },
    });
  }, [
    activePreferenceStorageKey,
    canAccessPreferences,
    invalidate,
    run,
    setError,
    setIsLoading,
  ]);

  const updatePreferences = async (updates: UpdateUserPreferences) => {
    setError(null);
    const requestStorageKey = activePreferenceStorageKey;

    const newPreferences = applyUserPreferenceUpdates(preferences, updates);
    setPreferences(newPreferences);
    if (activePreferenceStorageKey) {
      safeLocalStorage.setItem(
        activePreferenceStorageKey,
        JSON.stringify(newPreferences),
      );
    }

    if (canAccessPreferences) {
      try {
        const updatedPreferences = await mutateServerPreferences(updates);
        if ('_tag' in updatedPreferences && isRight(updatedPreferences)) {
          const normalizedPreferences = normalizeUserPreferences(
            updatedPreferences.right,
          );
          if (requestStorageKey !== activePreferenceStorageKeyRef.current) {
            if (requestStorageKey) {
              safeLocalStorage.setItem(
                requestStorageKey,
                JSON.stringify(normalizedPreferences),
              );
            }
            return;
          }

          setPreferences(normalizedPreferences);
          if (activePreferenceStorageKey) {
            safeLocalStorage.setItem(
              activePreferenceStorageKey,
              JSON.stringify(normalizedPreferences),
            );
          }
        } else {
          throw new Error('Failed to update preferences');
        }
      } catch (err) {
        if (requestStorageKey !== activePreferenceStorageKeyRef.current) {
          if (requestStorageKey) {
            safeLocalStorage.setItem(
              requestStorageKey,
              JSON.stringify(preferences),
            );
          }
          return;
        }

        captureAndReportError(err, {
          operation: 'Failed to update preferences on server',
          component: 'PreferencesProvider',
        });
        setError('Failed to save preferences');
        setPreferences(preferences);
        if (activePreferenceStorageKey) {
          safeLocalStorage.setItem(
            activePreferenceStorageKey,
            JSON.stringify(preferences),
          );
        }
        throw err;
      }
    }
  };

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        isLoading,
        error,
        updatePreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export const usePreferences = createRequiredContextHook(
  PreferencesContext,
  'PreferencesProvider',
  'usePreferences',
);
